import React, { useRef, useState } from "react";
import { FieldError, FieldValues, Path, UseFormRegister } from "react-hook-form";
import { mdiClose, mdiEye, mdiEyeOff, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { getClassByVariant } from "helpers/getClassByVariant";
import { useColorScheme } from "hooks/useColorScheme";
import styles from "./style.module.scss";

export interface IProps<T extends FieldValues>
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  name?: Path<T>;
  register?: UseFormRegister<T>;
  label?: string | React.ReactNode;
  className?: string;
  prefixNode?: React.ReactNode;
  prefixUnit?: string;
  suffixUnit?: string;
  inputClass?: string;
  labelClass?: string;
  isLoading?: boolean;
  disabled?: boolean;
  error?: FieldError;
  isHideError?: boolean;
  isPasswordIcon?: boolean;
  isSingleNumber?: boolean;
  isShowClose?: boolean;
  color?: "primary" | "secondary" | string;
  required?: boolean;
}

export default function Input<T extends FieldValues>({
  name,
  register,
  label,
  className,
  prefixNode,
  prefixUnit,
  suffixUnit,
  inputClass,
  labelClass,
  isLoading,
  disabled,
  error,
  isHideError = false,
  isPasswordIcon = false,
  isSingleNumber = false,
  isShowClose = false,
  color = "primary",
  autoComplete = "off",
  required = false,
  ...props
}: Readonly<IProps<T>>) {
  const [showPassword, setShowPassword] = useState<boolean>(!isPasswordIcon);
  const [value, setValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentIsDark } = useColorScheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (isSingleNumber && newValue.length > 1) {
      newValue = newValue.slice(0, 1);
    }
    setValue(newValue);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const getErrorMsg = () => {
    switch (error?.type) {
      case "email":
        return "This field value must be a valid email";

      case "required":
        return "This field is required";

      default:
        return null;
    }
  };

  return (
    <div className={clsx(styles.smartInputContainer, className)}>
      {label && (
        <label
          className={clsx(styles.smartInputLabel, labelClass, getClassByVariant(color)?.label)}>
          {required ? "* " : ""}
          {label}
        </label>
      )}
      <div className={styles.smartInputWrapper}>
        {prefixUnit && (
          <div className={styles.smartInputPrefixUnit}>
            <span dangerouslySetInnerHTML={{ __html: prefixUnit }} />
          </div>
        )}
        {prefixNode}
        <input
          type={isPasswordIcon ? (showPassword ? "text" : "password") : props.type}
          ref={inputRef}
          className={clsx(
            styles.smartInput,
            inputClass,
            getClassByVariant(color)?.input,
            currentIsDark ? "disabled:bg-input/50" : "disabled:bg-placeholder/40",
            !!disabled && "cursor-not-allowed",
            !!error && "!border-b-error",
            !!prefixUnit && "!rounded-s-none !pl-2.5",
            !!prefixNode && "!pl-12",
            !!suffixUnit && "!rounded-e-none !pr-2.5",
            !!isPasswordIcon && "!pr-12",
            { "!pr-10": isShowClose }
          )}
          autoComplete={autoComplete}
          disabled={disabled}
          {...(isSingleNumber && { value: value })}
          {...(isSingleNumber && { onInput: handleChange })}
          {...(register && name && register(name))}
          {...(props.type === "number" && { onWheel: (e: any) => e.target.blur() })}
          {...(props.type === "number" && { onFocus: (e: any) => e.target.select() })}
          {...props}
        />
        {isPasswordIcon && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className={styles.smartInputPasswordIcon}>
            {showPassword ? (
              <Icon path={mdiEyeOff} className="size-5" />
            ) : (
              <Icon path={mdiEye} className="size-5" />
            )}
          </button>
        )}
        {suffixUnit && (
          <div className={styles.smartInputSuffixUnit}>
            <span>{suffixUnit}</span>
          </div>
        )}
        {isLoading && <Icon spin path={mdiLoading} className={styles.smartInputLoader} />}
        {isShowClose && props.value && !isLoading && (
          <button
            onClick={() => {
              if (inputRef.current) inputRef.current.value = "";
            }}>
            <Icon path={mdiClose} className={styles.smartInputLoader} />
          </button>
        )}
      </div>
      {error && !isHideError && (
        <span className={styles.smartInputError}>{error.message ?? getErrorMsg()}</span>
      )}
    </div>
  );
}
